import React, { useEffect, useState } from "react";
import { adminAxios } from "../api";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, Youtube } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

// Extract a YouTube video/short ID from a full URL or return the raw id.
const parseYouTubeId = (input) => {
  if (!input) return "";
  const s = input.trim();
  const patterns = [/[?&]v=([\w-]{6,})/, /youtu\.be\/([\w-]{6,})/, /\/shorts\/([\w-]{6,})/, /\/embed\/([\w-]{6,})/];
  for (const p of patterns) { const m = s.match(p); if (m) return m[1]; }
  return s; // assume already an id
};

const MediaAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [video, setVideo] = useState({ kicker: "", title: "", youtubeId: "" });
  const [success, setSuccess] = useState({ kicker: "", title: "", shorts: [] });

  const load = async () => {
    setLoading(true);
    try {
      const cms = (await adminAxios.get("/cms")).data;
      const v = cms.find((c) => c.section === "video");
      const s = cms.find((c) => c.section === "success");
      if (v) setVideo({ kicker: "", title: "", youtubeId: "", ...v.data });
      if (s) setSuccess({ kicker: "", title: "", shorts: [], ...s.data });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveVideo = async () => {
    setSaving(true);
    try {
      await adminAxios.put("/cms/video", { section: "video", data: { ...video, youtubeId: parseYouTubeId(video.youtubeId) }, published: true });
      toast.success("Main video updated — live on website");
      load();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const saveSuccess = async () => {
    setSaving(true);
    try {
      const shorts = (success.shorts || []).slice(0, 5).map((sh) => ({ ...sh, id: parseYouTubeId(sh.id) })).filter((sh) => sh.id);
      await adminAxios.put("/cms/success", { section: "success", data: { ...success, shorts }, published: true });
      toast.success("Success stories updated — live on website");
      load();
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  const addShort = () => {
    if ((success.shorts || []).length >= 5) { toast("Maximum 5 shorts"); return; }
    setSuccess({ ...success, shorts: [...(success.shorts || []), { id: "", name: "", place: "" }] });
  };
  const setShort = (i, k) => (e) => {
    const list = [...(success.shorts || [])];
    list[i] = { ...list[i], [k]: e.target.value };
    setSuccess({ ...success, shorts: list });
  };
  const removeShort = (i) => setSuccess({ ...success, shorts: (success.shorts || []).filter((_, x) => x !== i) });

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-slate-400 animate-spin" /></div>;

  return (
    <div className="max-w-4xl">
      <h1 className="text-2xl font-semibold text-slate-900 mb-1">Videos &amp; Success Stories</h1>
      <p className="text-slate-500 mb-6">Manage the main trust video and the Success Stories shorts shown on the website.</p>

      {/* Main video */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-slate-800 flex items-center gap-2"><Youtube className="w-5 h-5 text-red-500" /> Main Trust Video</h2>
          <Button onClick={saveVideo} disabled={saving} className="bg-slate-900 hover:bg-slate-800">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save</Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-slate-600 text-sm mb-1.5 block">Section Kicker</Label><Input value={video.kicker || ""} onChange={(e) => setVideo({ ...video, kicker: e.target.value })} /></div>
          <div><Label className="text-slate-600 text-sm mb-1.5 block">Section Title</Label><Input value={video.title || ""} onChange={(e) => setVideo({ ...video, title: e.target.value })} /></div>
          <div className="sm:col-span-2"><Label className="text-slate-600 text-sm mb-1.5 block">YouTube URL or Video ID</Label><Input value={video.youtubeId || ""} onChange={(e) => setVideo({ ...video, youtubeId: e.target.value })} placeholder="https://youtu.be/... or video ID" /></div>
        </div>
        {video.youtubeId && (
          <div className="mt-4 aspect-video max-w-sm rounded-lg overflow-hidden border border-slate-200">
            <img src={`https://i.ytimg.com/vi/${parseYouTubeId(video.youtubeId)}/hqdefault.jpg`} alt="preview" className="w-full h-full object-cover" />
          </div>
        )}
      </div>

      {/* Success stories */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-slate-800">Success Stories (up to 5 shorts)</h2>
          <div className="flex gap-2">
            <Button variant="outline" onClick={addShort}><Plus className="w-4 h-4 mr-1" /> Add Short</Button>
            <Button onClick={saveSuccess} disabled={saving} className="bg-slate-900 hover:bg-slate-800">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save</Button>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div><Label className="text-slate-600 text-sm mb-1.5 block">Section Kicker</Label><Input value={success.kicker || ""} onChange={(e) => setSuccess({ ...success, kicker: e.target.value })} /></div>
          <div><Label className="text-slate-600 text-sm mb-1.5 block">Section Title</Label><Input value={success.title || ""} onChange={(e) => setSuccess({ ...success, title: e.target.value })} /></div>
        </div>
        <div className="mt-4 space-y-3">
          {(success.shorts || []).map((sh, i) => (
            <div key={i} className="rounded-lg border border-slate-200 p-3 bg-slate-50 flex gap-3 items-start">
              <div className="w-16 h-24 rounded-md overflow-hidden bg-slate-200 shrink-0">
                {sh.id ? <img src={`https://i.ytimg.com/vi/${parseYouTubeId(sh.id)}/hqdefault.jpg`} alt="" className="w-full h-full object-cover" /> : null}
              </div>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input value={sh.id || ""} onChange={setShort(i, "id")} placeholder="YouTube Short URL or ID" className="sm:col-span-3 border border-slate-200 rounded px-2 py-1.5 text-sm" />
                <input value={sh.name || ""} onChange={setShort(i, "name")} placeholder="Customer name" className="border border-slate-200 rounded px-2 py-1.5 text-sm" />
                <input value={sh.place || ""} onChange={setShort(i, "place")} placeholder="Location" className="border border-slate-200 rounded px-2 py-1.5 text-sm" />
                <button type="button" onClick={() => removeShort(i)} className="inline-flex items-center gap-1 text-red-500 text-sm hover:underline"><Trash2 className="w-4 h-4" /> Remove</button>
              </div>
            </div>
          ))}
          {(success.shorts || []).length === 0 && <p className="text-sm text-slate-400">No shorts yet. Click “Add Short”.</p>}
        </div>
      </div>
    </div>
  );
};

export default MediaAdmin;
